/**
 * Phase 3f.3 — Cloudflare Turnstile loader + token generator.
 *
 * Docs: https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
 *
 * Loaded lazily on first message of a new conversation. Subsequent
 * messages of the same conversation don't need a fresh token (worker
 * trusts the conversationId), so the script doesn't load at all when
 * the widget mounts — keeps idle bots feather-light.
 *
 * The script lives at https://challenges.cloudflare.com/turnstile/v0/api.js
 * and registers `window.turnstile` on load. We render into a hidden host
 * div and let the dashboard widget mode (set to "Managed" in the CF
 * Turnstile dashboard) decide whether to challenge the visitor. The JS
 * `size` parameter is intentionally omitted — as of mid-2026 CF rejects
 * `size: 'invisible'` and the rendering mode is dashboard-controlled.
 */

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ATTR = 'data-apaxir-turnstile';

interface TurnstileInstance {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      // Omit to let the dashboard widget mode decide. CF removed
      // 'invisible' as a valid JS value in mid-2026; visibility is now
      // controlled by the widget mode in the Turnstile dashboard.
      size?: 'normal' | 'compact' | 'flexible';
      // Theme to use; we let the host page decide.
      theme?: 'light' | 'dark' | 'auto';
      callback?: (token: string) => void;
      'error-callback'?: (code: string) => void;
      'expired-callback'?: () => void;
      'timeout-callback'?: () => void;
    },
  ) => string;
  execute: (widgetIdOrContainer: string | HTMLElement) => void;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileInstance;
  }
}

let scriptPromise: Promise<void> | null = null;

/**
 * Inject the Turnstile script tag once and resolve when `window.turnstile`
 * is available. Subsequent calls return the same promise so repeated
 * conversation restarts within one page lifetime don't re-load the script.
 */
function loadScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('turnstile_no_window'));
  }
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
    if (existing) {
      // Another widget instance already started the load; piggy-back.
      const onLoad = () => {
        existing.removeEventListener('load', onLoad);
        window.turnstile ? resolve() : reject(new Error('turnstile_no_global'));
      };
      existing.addEventListener('load', onLoad);
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.setAttribute(SCRIPT_ATTR, '1');
    script.onload = () => {
      window.turnstile
        ? resolve()
        : reject(new Error('turnstile_no_global'));
    };
    script.onerror = () => reject(new Error('turnstile_script_failed'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface GetTokenParams {
  sitekey: string;
  /** Max ms to wait for a token before giving up. CF challenges usually
   *  resolve in <1s; 15s is generous for slow networks. */
  timeoutMs?: number;
}

const HOST_ATTR = 'data-apaxir-turnstile-host';

/**
 * Reuse one light-DOM host across invocations so re-tries don't pile
 * up offscreen iframes.
 *
 * CF Turnstile (as of mid-2026) refuses to render in containers it deems
 * "non-renderable" and aborts with `turnstile_error_400020` ("Browser
 * refused render container"). Three patterns it rejects:
 *   - `display: none` — element has no box
 *   - `left: -9999px` / off-screen positioning — outside viewport
 *   - `width: 1px; height: 1px` — sub-pixel sizing
 *
 * The workaround is to keep a real-sized container in viewport but
 * visually hidden via `opacity: 0`. 300×65 matches the documented
 * Turnstile widget footprint. `pointer-events: none` + `z-index: -1`
 * keep it from intercepting clicks or stacking above page chrome.
 */
function ensureHost(): HTMLDivElement {
  let host = document.querySelector<HTMLDivElement>(`div[${HOST_ATTR}]`);
  if (host) return host;
  host = document.createElement('div');
  host.setAttribute(HOST_ATTR, '1');
  host.setAttribute('aria-hidden', 'true');
  host.style.position = 'fixed';
  host.style.bottom = '0';
  host.style.right = '0';
  host.style.width = '300px';
  host.style.height = '65px';
  host.style.opacity = '0';
  host.style.pointerEvents = 'none';
  host.style.zIndex = '-1';
  document.body.appendChild(host);
  return host;
}

/**
 * Render a Turnstile widget into a light-DOM host and resolve with the
 * resulting token. Light DOM is intentional — CF's iframe has known
 * issues inside shadow DOM, and the host is opacity:0 anyway.
 */
export async function getTurnstileToken(params: GetTokenParams): Promise<string> {
  await loadScript();
  const ts = window.turnstile;
  if (!ts) throw new Error('turnstile_unavailable');

  const container = ensureHost();
  // Clear any prior widget — re-tries should not stack hidden iframes.
  container.innerHTML = '';

  return await new Promise<string>((resolve, reject) => {
    const timeoutMs = params.timeoutMs ?? 15_000;
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('turnstile_timeout'));
    }, timeoutMs);

    try {
      ts.render(container, {
        sitekey: params.sitekey,
        // No `size` — CF removed 'invisible' as a valid value mid-2026.
        // Rendering mode is dashboard-driven ("Managed" widget mode).
        callback: (token: string) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(token);
        },
        'error-callback': (code: string) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(new Error(`turnstile_error_${code}`));
        },
        'expired-callback': () => {
          // Token expired before we used it. The widget can be re-
          // executed; surface as a regular error so caller retries.
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(new Error('turnstile_expired'));
        },
      });
    } catch (err) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(err instanceof Error ? err : new Error('turnstile_render_failed'));
    }
  });
}
